/**
 * Graphics bundles (SPEC §9.3, ADR 0002): tokens, fonts and assets for the fixed scene
 * set, drawn by the renderer the bundle names. Bundles come from this repo only, cannot
 * ship code, and are validated on every load: schema, file types and (in the graphics
 * app) that every referenced file exists, required palette roles, and font coverage.
 *
 * ```
 * bundles/<bundleId>/
 *   manifest.json   schemaVersion, bundleId, name, version, renderer, referenceResolution,
 *                   fonts (with use), assets, library, sponsors
 *   tokens.json     renderer, palette, strings, plus the renderer's own tokens
 *   fonts/  assets/
 * ```
 *
 * - v2 (Phase 4) replaced the single `assets.sponsor` with `sponsors[]`, rotating on the
 *   server clock (SPEC §17).
 * - v3 (Phase 5) adds `renderer`, palette roles instead of fixed `colors`, font `use`
 *   (`live` text must cover Portuguese), and the optional asset `library`.
 */
import { z } from 'zod';
import { PaletteSchema, RendererSchema } from './palette.ts';
import { parseOrThrow } from './validate.ts';

export const BUNDLE_SCHEMA_VERSION = 3;

/** Most sponsors one bundle rotates through. */
export const MAX_SPONSORS = 12;

/** Output frame rate: surfaces render at it and `signal` motion is timed in its frames (ADR 0002). */
export const FRAME_RATE = 30;

const kebab = z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'must be kebab-case');
/** A bundle-relative file in an allowed folder with an allowed type. No `..`, no absolute paths. */
const bundleFile = (folder: 'fonts' | 'assets', exts: readonly string[]) =>
  z
    .string()
    .max(200)
    .regex(new RegExp(`^${folder}/[A-Za-z0-9_-]+(/[A-Za-z0-9_-]+)*\\.(${exts.join('|')})$`), `must be a ${folder}/… file of type ${exts.join(', ')}`);

export const ASSET_TYPES = ['svg', 'png', 'webp', 'webm'] as const;
const asset = bundleFile('assets', ASSET_TYPES);

/** Named assets the components look up; bundles may add more names. Sponsors are listed separately. */
export const ASSET_NAMES = ['background', 'logo'] as const;

/**
 * Where a font may be used. `live` fonts draw sheet data and typed text (names, titles,
 * comments), so they must cover printable Basic Latin and Latin-1. `display` fonts only
 * draw fixed strings, each checked against the font (SPEC §9.3).
 */
export const FontUseSchema = z.enum(['live', 'display']);
export type FontUse = z.infer<typeof FontUseSchema>;

/** A single font family name (no stacks: a `signal` bundle has no fallback fonts). */
const familyName = z.string().regex(/^[A-Za-z0-9 '-]+$/, 'must be one plain font-family name').max(100);

export const BundleManifestSchema = z.strictObject({
  schemaVersion: z.literal(BUNDLE_SCHEMA_VERSION, {
    error: `must be ${BUNDLE_SCHEMA_VERSION}: schema 3 added "renderer", palette roles and font "use" (see packages/shared/src/bundle.ts and docs/adr/0002-style-renderers.md)`,
  }),
  bundleId: kebab,
  name: z.string().min(1).max(100),
  version: z.string().regex(/^\d+\.\d+\.\d+$/, 'must be a semantic version'),
  /** Which renderer draws this bundle (ADR 0002). Must match `tokens.json`. */
  renderer: RendererSchema,
  referenceResolution: z.strictObject({ width: z.literal(1920), height: z.literal(1080) }),
  fonts: z
    .array(
      z.strictObject({
        family: familyName,
        file: bundleFile('fonts', ['woff2']),
        weight: z.number().int().min(100).max(900),
        style: z.enum(['normal', 'italic']),
        use: FontUseSchema,
      }),
    )
    .max(16),
  assets: z.record(kebab, asset),
  /** The decorative asset library (`assets/library.json`), or `null`. Required by `signal`. */
  library: z
    .string()
    .regex(/^assets\/[A-Za-z0-9_-]+\.json$/, 'must be an assets/….json file')
    .nullable(),
  /** Shown in the sponsor box in this order, one at a time (SPEC §17). */
  sponsors: z
    .array(z.strictObject({ name: z.string().trim().min(1).max(100), file: asset }))
    .min(1)
    .max(MAX_SPONSORS),
  variants: z.array(kebab).max(16),
});
export type BundleManifest = z.infer<typeof BundleManifestSchema>;

const px = z.number().min(0).max(1000);
const ms = z.number().int().min(0).max(10_000);
/** CSS easing keyword or cubic-bezier(). */
const easing = z.string().regex(/^(ease|ease-in|ease-out|ease-in-out|linear|cubic-bezier\(\s*-?[\d.]+\s*,\s*-?[\d.]+\s*,\s*-?[\d.]+\s*,\s*-?[\d.]+\s*\))$/, 'must be a CSS easing');
const fontStack = z.string().regex(/^[A-Za-z0-9 ,"'-]+$/, 'must be a plain font-family list').max(300);

/** Fixed on-screen copy every renderer draws. */
export const STRING_KEYS = ['startingSoon', 'breakTitle', 'technicalDifficulties', 'technicalDifficultiesSub', 'nextSession', 'endScreen', 'endScreenSub', 'sponsorLabel'] as const;
export type StringKey = (typeof STRING_KEYS)[number];

/** On-screen copy: the bundle owns wording and language, not the code. */
const StringsSchema = z.strictObject(Object.fromEntries(STRING_KEYS.map((k) => [k, z.string().max(200)])) as Record<StringKey, z.ZodString>);

/** Tokens of the `dom` renderer (the Phase 2 components; the starter bundle). */
export const DomTokensSchema = z.strictObject({
  renderer: z.literal('dom'),
  palette: PaletteSchema,
  typography: z.strictObject({
    fontFamily: fontStack,
    headingFamily: fontStack,
    /** Sizes in px at 1920×1080. */
    size: z.strictObject({ nameplate: px, nameplateSub: px, lowerThirdName: px, lowerThirdSub: px, title: px, subtitle: px, timer: px, body: px }),
    weight: z.strictObject({ regular: z.number().int().min(100).max(900), bold: z.number().int().min(100).max(900) }),
  }),
  spacing: z.strictObject({ xs: px, sm: px, md: px, lg: px, xl: px }),
  radii: z.strictObject({ frame: px, plate: px }),
  frame: z.strictObject({ width: px }),
  motion: z.strictObject({
    /** Text changes on air animate with this (SPEC §9.4). */
    textTransitionMs: ms,
    lowerThirdInMs: ms,
    lowerThirdOutMs: ms,
    toggleMs: ms,
    /** How long each sponsor stays up before the next one. */
    sponsorRotateMs: z.number().int().min(1_000).max(120_000),
    /** Crossfade between sponsors; shorter than the rotation. */
    sponsorFadeMs: ms,
    easing,
  }),
  strings: StringsSchema,
});
export type DomTokens = z.infer<typeof DomTokensSchema>;

/** Frames at {@link FRAME_RATE}. */
const frames = z.number().int().min(0).max(1800);
const frameRange = z
  .strictObject({ min: z.number().int().min(1).max(120), max: z.number().int().min(1).max(120) })
  .refine((r) => r.min <= r.max, { message: 'min must not exceed max' });
const fontRole = z.strictObject({ family: familyName, weight: z.number().int().min(100).max(900) });
const densityProfile = z.strictObject({
  /** Most decorative elements alive at once. */
  elementBudget: z.number().int().min(0).max(2000),
  /** Ambient micro-events per second at full intensity. */
  ambientEventsPerSecond: z.number().min(0).max(30),
  /** Glitch events per minute at full intensity. */
  glitchesPerMinute: z.number().min(0).max(120),
});

/** Font roles of the `signal` renderer. `primary` and `secondary` draw live data. */
export const SIGNAL_FONT_ROLES = ['primary', 'secondary', 'display', 'symbol'] as const;
export type SignalFontRole = (typeof SIGNAL_FONT_ROLES)[number];
/** Font roles that draw sheet data or typed text: their families must be `live` fonts. */
export const SIGNAL_LIVE_FONT_ROLES = ['primary', 'secondary'] as const satisfies readonly SignalFontRole[];

/** Density profiles (ADR 0002): how much the procedural environment does. */
export const DENSITIES = ['low', 'normal', 'high', 'alert'] as const;
export const DensitySchema = z.enum(DENSITIES);
export type Density = z.infer<typeof DensitySchema>;

/** Tokens of the `signal` renderer (PixiJS, procedural background, frame-timed motion). */
export const SignalTokensSchema = z.strictObject({
  renderer: z.literal('signal'),
  palette: PaletteSchema,
  typography: z.strictObject({
    primary: fontRole,
    secondary: fontRole,
    display: fontRole,
    symbol: fontRole,
    /** Sizes in px at 1920×1080. */
    size: z.strictObject({ nameplate: px, nameplateSub: px, lowerThirdName: px, lowerThirdSub: px, title: px, subtitle: px, timer: px, body: px, label: px }),
    /** Which font role draws each fixed string (checked for glyph coverage). */
    strings: z.strictObject(Object.fromEntries(STRING_KEYS.map((k) => [k, z.enum(['primary', 'secondary', 'display'])])) as Record<StringKey, z.ZodEnum<{ primary: 'primary'; secondary: 'secondary'; display: 'display' }>>),
  }),
  /** Timing classes in frames (BRIEF: MICRO, FAST, NORMAL, SYSTEM). */
  timing: z.strictObject({ micro: frameRange, fast: frameRange, normal: frameRange, system: frameRange }),
  /** Gradual start when a surface goes on program (SPEC §9.4). */
  warmup: z.strictObject({
    /** Staggered build of decoration after the calm base frame. */
    buildFrames: frames,
    /** Primary text (titles, names) starts resolving at this frame. */
    primaryAtFrame: frames,
    /** Ambient intensity climbs from 0 to the density profile over this many frames. */
    ambientRampFrames: frames,
    /** No glitch or alert event until this many frames after the warm-up ends. */
    alertGraceFrames: frames,
  }),
  density: z.strictObject({
    /** A density change ramps over this many frames (never a one-frame jump). */
    rampFrames: frames,
    /** Ramp into `alert`: faster, still never a one-frame jump. */
    alertRampFrames: z.number().int().min(2).max(1800),
    profiles: z.strictObject({ low: densityProfile, normal: densityProfile, high: densityProfile, alert: densityProfile }),
  }),
  /** Smallest features that survive 6 Mbps H.264 (ADR 0002). */
  minFeaturePx: z.strictObject({ line: z.number().min(2).max(64), cell: z.number().min(8).max(256) }),
  sponsor: z.strictObject({ rotateMs: z.number().int().min(1_000).max(120_000) }),
  strings: StringsSchema,
});
export type SignalTokens = z.infer<typeof SignalTokensSchema>;

export const BundleTokensSchema = z.discriminatedUnion('renderer', [DomTokensSchema, SignalTokensSchema]);
export type BundleTokens = z.infer<typeof BundleTokensSchema>;

/** Categories of the decorative asset library (the brief's visual vocabulary). */
export const ASSET_CATEGORIES = ['block', 'wireframe', 'graph', 'signal', 'data', 'warning', 'label', 'ornament', 'symbol'] as const;
export const AssetCategorySchema = z.enum(ASSET_CATEGORIES);
export type AssetCategory = z.infer<typeof AssetCategorySchema>;

/**
 * `assets/library.json`: single-colour (white) SVGs tinted by palette role at runtime,
 * generated by `scripts/style-assets.ts`.
 */
export const AssetLibrarySchema = z.strictObject({
  schemaVersion: z.literal(1),
  assets: z
    .array(
      z.strictObject({
        id: kebab,
        file: bundleFile('assets', ['svg']),
        category: AssetCategorySchema,
        tags: z.array(kebab).max(16),
        /** viewBox size. */
        width: z.number().positive().max(10_000),
        height: z.number().positive().max(10_000),
        bytes: z.number().int().min(1).max(64 * 1024),
      }),
    )
    .max(2000),
});
export type AssetLibrary = z.infer<typeof AssetLibrarySchema>;

export function parseBundleManifest(data: unknown, where = 'manifest.json'): BundleManifest {
  return parseOrThrow(BundleManifestSchema, data, `bundle manifest "${where}"`);
}

export function parseBundleTokens(data: unknown, where = 'tokens.json'): BundleTokens {
  return parseOrThrow(BundleTokensSchema, data, `bundle tokens "${where}"`);
}

export function parseAssetLibrary(data: unknown, where = 'library.json'): AssetLibrary {
  return parseOrThrow(AssetLibrarySchema, data, `asset library "${where}"`);
}
